import React from "react";

import { isArray } from "lodash";
import { Controller } from "react-hook-form-7";

import {
  Box,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Stack,
  StackDivider,
  Avatar,
  Text,
} from "@chakra-ui/react";
import FormDescription from "../../../utils/chakra/FormDescription";
import CheckboxCardGroup from "../../../utils/chakra/CheckboxCardGroup";

import useTranslations from "../../../../hooks/useTranslations";
import useStore from "../../../../store";
import useQuestionErrors from "../../hooks/useQuestionErrors";
import usePreviousAnswers from "../../hooks/usePreviousAnswers";

import AnswerEmpty from "../answers/AnswerEmpty";
import AnswerSelectorMultipleReview from "../answers/AnswerSelectorMultipleReview";
import QuestionActions from "./QuestionActions";
import QuestionCommonInputs from "./QuestionCommonInputs";

import { QuestionProps } from "./types/QuestionProps";
import { AnswerFragment } from "../../../../graphql/generated/definitions";

const QuestionSelectorMultiple: React.FC<QuestionProps> = ({
  register,
  question,
  report,
  path,
  control,
  ...restProps
}) => {
  const t = useTranslations();
  const viewer = useStore((state) => state.viewer);
  const previousAnswers = usePreviousAnswers(
    question,
    report
  ) as AnswerFragment[];
  const { isInvalid, errorMessage } = useQuestionErrors({
    control,
    name: `${path}.value`,
  });

  return (
    <Box>
      <FormControl
        as="fieldset"
        isInvalid={isInvalid}
        isRequired={question.required}
        id={`${path}.value`}
      >
        <FormLabel as="legend" fontWeight="semibold" fontSize="xl">
          {restProps.sectionIndex + 1}.{restProps.questionIndex + 1}
          {" • "}
          {question.label}
        </FormLabel>
        {question.description && (
          <FormDescription>{question.description}</FormDescription>
        )}
        <QuestionActions {...{ question, report }} />
        <Stack spacing="7" divider={<StackDivider maxW="2xl" />}>
          {previousAnswers.map((answer) =>
            answer.isEmpty ? (
              <AnswerEmpty
                key={answer.id}
                path={path}
                answer={answer}
                question={question}
              />
            ) : (
              <AnswerSelectorMultipleReview
                key={answer.id}
                path={path}
                answer={answer}
                question={question}
              />
            )
          )}
          <Stack spacing="4">
            {previousAnswers.length > 0 && (
              <Stack spacing="3" alignItems="end" direction="row">
                <Avatar
                  size="sm"
                  src={viewer.avatar}
                  alt={viewer.name}
                  name={viewer.name}
                />
                <Text fontWeight="medium" fontSize="md" color="gray.600">
                  {t("reports.edit.yourAnswer")}
                </Text>
              </Stack>
            )}

            <Controller
              control={control}
              name={`${path}.value` as const}
              rules={{
                required:
                  question.required &&
                  (t("reports.shared.requiredQuestion") as string),
              }}
              render={({ field: { value, ...fieldMethods } }) => (
                <CheckboxCardGroup
                  {...fieldMethods}
                  isInvalid={isInvalid}
                  value={isArray(value) ? value : [value]}
                  options={question.options}
                />
              )}
            />
            <FormErrorMessage color="invalid.500">{errorMessage}</FormErrorMessage>
            <QuestionCommonInputs
              config={{
                hasComment: true,
                hasTimeTracked: question.timeTracked,
              }}
              {...{ register, path, control, question, report, ...restProps }}
            />
          </Stack>
        </Stack>
      </FormControl>
    </Box>
  );
};

export default React.memo(QuestionSelectorMultiple);
